import Header from '@/components/Header/Header';
import StarTooltip from '@/components/Icons/CustomIcons/StarTooltip';
import Icon from '@/components/Icons/Icon';
import Link from '@/components/Link/Link';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useBundles } from '@/services/bundles/bundles.service';
import { BundleDetails } from '@/types/bundles.types';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import LinkIcon from '@mui/icons-material/Link';
import { CellContext, Row } from '@tanstack/react-table';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BundleLibrary.module.css';

const DisplayWithStarTooltip = ({
  id,
  bundleCode,
  isLinked,
}: {
  id: string;
  bundleCode: string;
  isLinked: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.starToolTipAndBundleCodeWrapper}>
      <StarTooltip content={t('bundleLibrary.nationalProgram')} className={styles.starSvg} position="top" />
      <div className={styles.bundleCodeAndLinkIconWrapper}>
        <Link to={`/bundles/${id}`}>{bundleCode}</Link>
        {isLinked && <LinkIcon style={{ fontSize: '20px', color: '#266DCC' }} />}
      </div>
    </div>
  );
};
const ViewBundle = ({ row }: CellContext<BundleDetails, unknown>) =>
  row.original.isNationalProgramLiveOrOptIn ? (
    <DisplayWithStarTooltip
      id={row.original.id}
      bundleCode={row.original.code}
      isLinked={!!row.original.linkedBundleName}
    />
  ) : (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Link to={`/bundles/${row.original.id}`}>{row.original.code}</Link>
      {row.original.linkedBundleName && <LinkIcon style={{ fontSize: '20px', color: '#266DCC' }} />}
    </div>
  );

const PriceRange = ({ row }: CellContext<BundleDetails, unknown>) => {
  const { t } = useTranslation();
  if (row.original.availablePrices?.length > 0) {
    const sortedPrices = row.original.availablePrices?.sort((a: number, b: number) => a - b);
    return sortedPrices.length <= 1
      ? `$${sortedPrices[0].toFixed(2)}`
      : `$${sortedPrices[0].toFixed(2)} - $${sortedPrices[sortedPrices.length - 1].toFixed(2)}`;
  }
  return (
    <div className={styles.priceError}>
      <Icon iconName="AlertCircle" className={styles.icon} />
      {t('common.bundle.missingPricePointsLabel')}
    </div>
  );
};

const BundleLibrary: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useBundles(true);

  if (isError) {
    showToast({
      label: t('error.fetchingBundles'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingBundles',
    });
  }

  return (
    <>
      <Header title={t('bundleLibrary.title')} breadcrumb={{ label: t('breadcrumb.backToHome'), to: '/' }} />
      <div className={styles.pageContainer}>
        <TableCard
          width="calc(100vw - 250px)"
          title={t('bundleLibrary.table.title')}
          breadcrumb={{ label: t('bundleLibrary.table.breadcrumb'), to: '/bundles/library/create-linked-bundle' }}
          isLoading={isLoading}
          data={data?.data || []}
          columns={[
            {
              accessorFn: (originalRow: BundleDetails) => originalRow.code || '-',
              header: t('bundleLibrary.table.bundleCode'),
              enableSorting: true,
              cell: ViewBundle,
            },
            {
              accessorFn: (originalRow: BundleDetails) => originalRow.name || '-',
              header: t('bundleLibrary.table.displayName'),
              enableSorting: true,
            },
            {
              accessorFn: (originalRow: BundleDetails) => originalRow.internalName || '-',
              header: t('bundleLibrary.table.bundleDetail'),
              enableSorting: true,
            },
            {
              accessorKey: 'availablePrices',
              header: t('bundleLibrary.table.priceRange'),
              cell: PriceRange,
              filterFn: (row: Row<BundleDetails>, columnId: string, filterValue) => {
                const availPrices = row.original.availablePrices;
                if (availPrices && availPrices.toString().includes(filterValue)) return true;
                return false;
              },
              enableSorting: true,
            },
            {
              accessorKey: 'fzGroupCount',
              header: t('bundleLibrary.table.activeStores'),
              enableSorting: true,
              cell: (props) => {
                if (props.row.original.fzGroupCount !== null) {
                  return props.row.original.fzGroupCount;
                }
                return 0;
              },
            },
            {
              accessorKey: 'occasions',
              header: t('bundleLibrary.table.occasions'),
              cell: ({ row }) => {
                const { occasions } = row.original;
                return renderOccasions(t, occasions);
              },
              enableSorting: true,
              meta: { disableFilter: true },
            },
            {
              accessorKey: 'isVisible',
              header: t('bundleLibrary.table.visibility'),
              cell: ({ row }) => (row.original.isVisible ? t('common.visible') : t('common.invisible')),
              enableSorting: true,
              meta: { disableFilter: true },
            },
          ]}
          returnedRecordsText={(count) => t('bundleLibrary.table.returnedRecord', { count })}
          enableFilters
        />
      </div>
    </>
  );
};

export default BundleLibrary;
